// Global Instructions Rule Applied!
//
// Statement Runs section of the Statements tab (spec A3.14 stage 5, part 3):
// the statement runs this fund - or this consolidated fund - is in, newest
// period first, each a link to its run form, where the stored statements are
// reviewed, marked as issued and downloaded.
//
// The preview above it is a live rendering; this table is where the STORED
// statements of a period are found. A legal entity of a consolidated fund is
// shown the runs of that consolidated fund: its statements are made there.
//
// A fund's run also offers its Review workbook: the fund's books as of the
// run's period end, as one working paper for the reviewer (the statements,
// the schedule of investments, the partners' capital, the trial balance and
// the journal lines since inception, linked by formula). Built live, so it
// reads the books as they are now, not as the stored statements were made.

import React, { useCallback, useMemo, useState } from 'react'
import { Alert, Button, Table, Tag, Tooltip, Typography } from 'antd'
import { FileExcelOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { downloadFundReviewWorkbook } from '../../../services/fundsService.ts'
import { useTabManagerContext } from '../../../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_INVESTOR_STATEMENTS } from '../../../../../state_manager/globals.ts'
import { useFundStatementRuns } from '../../../../investor_statements/hooks/useFundStatementRuns.ts'
import { issuedText, runFundStateTag } from '../../../../investor_statements/utils/runDisplay.ts'
import type { FundStatementRun } from '../../../../../state_manager/data_models/investor_statement_run.ts'

const { Text } = Typography

const RECORD_LINK_CLASS =
  'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'

interface FundStatementRunsSectionProps {
  /** The fund whose runs these are ... */
  fundId?: string | null
  /** ... or the consolidated fund. Exactly one of the two. */
  fundGroupId?: string | null
}

export default function FundStatementRunsSection({ fundId, fundGroupId }: FundStatementRunsSectionProps) {
  const { openFormInTab } = useTabManagerContext()
  const { runs, loading, error } = useFundStatementRuns(fundId, fundGroupId)
  const [downloadingPeriod, setDownloadingPeriod] = useState<string | null>(null)
  const handleReviewWorkbook = useCallback(
    async (periodEnd: string) => {
      if (!fundId) return
      setDownloadingPeriod(periodEnd)
      try {
        const res = await downloadFundReviewWorkbook(fundId, periodEnd, getAccessToken(store.getState().auth))
        if (!res.success) showErrorToast(res.error || 'Failed to download the review workbook')
      } finally {
        setDownloadingPeriod(null)
      }
    },
    [fundId]
  )

  const columns = useMemo(
    (): ColumnsType<FundStatementRun> => [
      {
        title: 'Run',
        key: 'run',
        render: (_: unknown, row) => (
          <div>
            <button
              type='button'
              className={RECORD_LINK_CLASS}
              onClick={() =>
                openFormInTab(TABLE_INVESTOR_STATEMENTS, row.investor_statement_runs__id, row.investor_statement_runs__name)
              }
            >
              {row.investor_statement_runs__name}
            </button>
            {row.prog__via_name ? <Text type='secondary'> · with {row.prog__via_name}</Text> : null}
          </div>
        )
      },
      { title: 'Period', key: 'period', width: 120, render: (_: unknown, row) => row.prog__period_label },
      {
        title: 'Status',
        key: 'status',
        width: 220,
        render: (_: unknown, row) => {
          const tag = runFundStateTag(row)
          return (
            <Tooltip title={tag.tooltip}>
              <Tag color={tag.color}>{tag.text}</Tag>
            </Tooltip>
          )
        }
      },
      {
        title: 'Statements',
        key: 'statements',
        width: 120,
        align: 'right',
        render: (_: unknown, row) =>
          row.prog__counts.total > 0 ? `${row.prog__counts.generated} of ${row.prog__counts.total}` : '—'
      },
      { title: 'Issued', key: 'issued', width: 260, render: (_: unknown, row) => issuedText(row) },
      ...(fundId
        ? [
            {
              title: '',
              key: 'review',
              width: 160,
              render: (_: unknown, row: FundStatementRun) => (
                <Tooltip title='The books as of this period end, as one workbook for the reviewer: the statements, the schedule of investments, the partners capital, the trial balance and the journal lines, linked by formula'>
                  <Button
                    size='small'
                    icon={<FileExcelOutlined />}
                    loading={downloadingPeriod === row.investor_statement_runs__period_end}
                    onClick={() => void handleReviewWorkbook(row.investor_statement_runs__period_end)}
                  >
                    Review workbook
                  </Button>
                </Tooltip>
              )
            }
          ]
        : [])
    ],
    [openFormInTab, fundId, downloadingPeriod, handleReviewWorkbook]
  )

  return (
    <FormSection title='Statement Runs' titleColor='green' loading={loading && !runs} className='mt-3'>
      {error && !runs ? (
        <Alert type='error' showIcon message='Failed to load the statement runs' description={error} />
      ) : !runs || runs.length === 0 ? (
        <Text type='secondary'>
          {fundGroupId ? 'This consolidated fund' : 'This fund'} is in no statement run yet. A run stores a quarter&apos;s
          statements: Operations, Investor Statements.
        </Text>
      ) : (
        <Table<FundStatementRun>
          size='small'
          pagination={false}
          rowKey='investor_statement_run_funds__id'
          columns={columns}
          dataSource={runs}
        />
      )}
    </FormSection>
  )
}
